// Game directory: signed-out shows a Google sign-in prompt; signed-in shows
// any in-progress games (with rejoin links), the list of games, and external
// favorites.

import { GAME_DIRECTORY, ResumeGames, useAuth, type GameEntry } from "@couplesgames/frontend-core";

const EXTERNAL_GAMES: GameEntry[] = [
  {
    slug: "color-match",
    name: "Color Match",
    url: "https://dialed.gg",
    description: "Match colors based on memory.",
  },
  {
    slug: "maptap",
    name: "MapTap",
    url: "https://maptap.gg",
    description: "Identify the place of a country on a map without borders.",
  },
];

function displayName(profile: { name?: string; email?: string } | null): string {
  if (!profile) return "Signed in";
  if (profile.name) return profile.name;
  if (profile.email) return profile.email;
  return "Signed in";
}

export function GameDirectory(): JSX.Element {
  const { ready, session, profile, login } = useAuth();

  if (!ready) {
    return (
      <main className="flex min-h-dvh items-center justify-center text-slate-400">
        loading…
      </main>
    );
  }

  if (!session) {
    return (
      <main className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center px-6">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight">Couples Games</h1>
            <p className="text-slate-400">Two-player games, built to play together.</p>
          </div>
          <button
            onClick={() => void login("/")}
            className="w-full rounded-xl bg-white text-slate-900 font-semibold py-3 hover:bg-slate-200 transition"
          >
            Sign in with Google
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="px-6 py-12">
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <header className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">Couples Games</h1>
          <p className="text-slate-400">
            Signed in as {displayName(profile)}. Pick a game to get started.
          </p>
        </header>

        <ResumeGames />

        <div className="grid gap-4 sm:grid-cols-2">
          {GAME_DIRECTORY.map((game) => (
            <GameCard key={game.url} game={game} />
          ))}
        </div>

        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold">External games</h2>
            <p className="text-slate-400 text-sm">A few favorite games from elsewhere on the web.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {EXTERNAL_GAMES.map((game) => (
              <GameCard key={game.url} game={game} />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

function GameCard({ game }: { game: GameEntry }): JSX.Element {
  return (
    <a
      href={game.url}
      className="group rounded-xl border border-slate-800 bg-slate-900/50 p-5 transition hover:border-slate-600"
    >
      <h3 className="font-semibold text-slate-100 group-hover:text-sky-400 transition">{game.name}</h3>
      <p className="mt-1 text-sm text-slate-400">{game.description}</p>
      <span className="mt-3 inline-block text-sm font-medium text-sky-400">Play →</span>
    </a>
  );
}
